import React, { useState, useEffect, useMemo } from "react";
import { Button, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, IconButton } from "@material-ui/core";
import { GetApp as GetAppIcon } from "@material-ui/icons";
import { File, Text } from "components/FormComponents";
import Modal from "components/Modal/Modal";
import { format } from "currency";
import { Date as DateComponent } from "components/Core";
import { enviarSiniestroRevision, get, enviarSiniestroRevisionFiniquito } from "api";
import queryClient from "api/query";
import { SEGURO, SINIESTRO } from "api/TYPES";
import useSave from "Hooks/useSave";
import { crudFunctions } from "api";
import { useRouteMatch } from "react-router-dom";
import QRCode from "react-qr-code";

import AutoSaveStatus from "components/AutoSaveStatus/AutoSaveStatus";
import { useQuery } from "react-query";

const generalRequiredDocuments = [
	{ key: "historial_gps", label: "Historial de GPS de la ruta de la unidad", required: true },
	{ key: "salida_almacen", label: "Salida del almacén / Packing list", required: true },
	{ key: "factura_compra", label: "Factura de compra o venta / documento que acredite propiedad de los bienes", required: true },
	{ key: "licencia_conductor", label: "Licencia de conducir del operador", required: true },
	{ key: "tarjeta_circulacion", label: "Tarjeta de circulación del vehículo", notes:"En caso de que se realizó el viaje con un vehículo que lleva remolque, se debe adjuntar la tarjeta de circulación de la misma adicional a la del tracto", required: true },
	{ key: "carta_porte", label: "Carta porte / factura del flete", required: true },
	{ key: "declaracion_mp", label: "Declaración ante el MP", required: true },
	{ key: "carta_reclamacion_chubb", label: <span>Carta de reclamación de Chubb <a target="_blank" style={{textDecoration:"underline", color:"blue"}} href={`${process.env.REACT_APP_FILE_URL}files/plantilla_reclamacion_chubb.docx`}>Plantilla reclamación chubb</a></span>, required: true },
	{ key: "carta_reclamacion_transportista", label: <span>Carta de reclamación ante transportista <a target="_blank" style={{textDecoration:"underline", color:"blue"}} href={`${process.env.REACT_APP_FILE_URL}files/plantilla_reclamacion_transportista.docx`}>Plantilla reclamación transportista</a></span>, required: true }
];

const containerRequiredDocuments = [
	{ key: "carta_reclamacion_chubb", label: <span>Carta de reclamación a Chubb <a target="_blank" rel="noopener noreferrer" style={{textDecoration:"underline", color:"blue"}} href={`${process.env.REACT_APP_FILE_URL}files/plantilla_reclamacion_chubb.docx`}>Descargar formato</a></span>, required: true },
	{ key: "declaracion_mp", label: "Acta ante el MP de los hechos, ya sea robo, accidente, etc.", required: true },
	{ key: "carta_cobro_naviera", label: "Carta de la naviera dirigida al asegurado en la que se realiza el cobro del contenedor", required: true },
	{ key: "licencia_conductor", label: "Licencia del operador de la unidad en la que venía el contenedor", required: true },
	{ key: "tarjeta_circulacion", label: "Tarjeta de circulación de la unidad y del remolque en los que venía el contenedor", required: true },
	{ key: "carta_porte", label: "Carta Porte del trayecto", required: true },
	{ key: "historial_gps", label: "Histórico de GPS", required: true },
];


// MODIFIED: Progress Bar Component
const SiniestroProgressBar = ({ currentStatus, onStepClick, viewedStep }) => {
    const steps = [
			{ key: "pendiente_documentacion", label: "Pendiente Doc." },
			{ key: "en_revision_documentacion", label: "Revisión Doc." },
			{ key: "pendiente_finiquito", label: "Pendiente Finiquito" },
			{ key: "en_revision_finiquito", label: "En espera de pago" },
			{ key: "pagado", label: "Pagado" }
		];

    const isRejected = currentStatus === 'rechazado';
    const currentStepIndex = steps.findIndex(step => step.key === currentStatus);

    return (
        <div className="mb-8">
            <div className="flex items-center justify-between">
                {steps.map((step, index) => {
                    const isCompleted = !isRejected && index < currentStepIndex;
                    const isCurrent = !isRejected && index === currentStepIndex;
										const isViewed = viewedStep === step.key;

                    return (
                        <React.Fragment key={step.key}>
                            <div className="flex flex-col items-center cursor-pointer" onClick={() => onStepClick(step.key)}>
                                <div
                                    className={`
                                        w-8 h-8 rounded-full flex items-center justify-center text-white
                                        ${isCompleted ? 'bg-green-500' : ''}
                                        ${isCurrent ? 'bg-blue-500' : ''}
                                        ${!isCompleted && !isCurrent && !isRejected ? 'bg-gray-300' : ''}
                                        ${isRejected ? 'bg-gray-300' : ''}
																				${isViewed ? 'ring-2 ring-offset-2 ring-indigo-500' : ''}
                                    `}
                                >
                                    {isCompleted ? '✔' : index + 1}
                                </div>
                                <p className={`mt-2 text-xs text-center ${isViewed ? 'font-bold text-indigo-600' : isCurrent ? 'font-bold text-blue-600' : 'text-gray-500'}`}>
                                    {step.label}
                                </p>
                            </div>
                            {index < steps.length - 1 && (
                                <div className={`flex-1 h-1 mx-2 ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            )}
                        </React.Fragment>
                    );
                })}
				{isRejected && (
					<>
						<div className="flex-1 h-1 mx-2 bg-red-500"></div>
						<div className="flex flex-col items-center">
							<div className="w-8 h-8 rounded-full flex items-center justify-center bg-red-500 text-white">
								!
							</div>
							<p className="mt-2 text-xs text-center font-bold text-red-600">
								Rechazado
							</p>
						</div>
					</>
				)}
            </div>
        </div>
    );
};


const DocumentStatus = ({ status, notes }) => {
	const getStatusColor = (status) => {
		switch (status) {
			case 'accepted': return { color: '#47CAA9', bg: '#D2FBF0' };
			case 'rejected': return { color: '#F42829', bg: '#FDD4D4' };
			case 'in_review': return { color: '#3363FF', bg: '#CCD8FF' };
			case 'pending': return { color: '#3363FF', bg: '#D8FF26' };
			default: return { color: '#FF8427', bg: '#FFCDA8' };
		}
	};

	const getStatusLabel = (status) => {
		switch (status) {
			case 'accepted': return 'Aceptado';
			case 'rejected': return 'Rechazado';
			case 'in_review': return 'En Revisión';
			case 'pending': return 'Subido';
			default: return 'Pendiente';
		}
	};

	const statusColor = getStatusColor(status);

	return (
		<div className="mt-2">
			<span
				className="px-2 py-1 rounded text-xs font-medium"
				style={{ color: statusColor.color, backgroundColor: statusColor.bg }}
			>
				{getStatusLabel(status)}
			</span>
			{status === 'rejected' && notes && (
				<div className="mt-1 text-sm text-red-600 bg-red-50 p-2 rounded">
					<strong>Motivo de rechazo:</strong> {notes}
				</div>
			)}
		</div>
	);
};

const SiniestroModal = ({ onClose }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [viewedStep, setViewedStep] = useState(null);
	const [history, setHistory] = useState([]);
	const match = useRouteMatch();
	
	// Load siniestro with all associated seguros
	const { data: siniestroData } = useQuery(
		['siniestro', match.params.id], 
		() => get(`${SEGURO.url}/siniestro/${match.params.id}`)
	);
	
	// Calculate total valor_mercancia from all seguros
	const totalValorMercancia = useMemo(() => {
		return siniestroData?.seguros?.reduce((acc, s) => acc + (s?.valor_mercancia || 0), 0) || 0;
	}, [siniestroData]);

	useEffect(() => {
		if (siniestroData) {
			setViewedStep(siniestroData?.status);
			// This is a simplified history generation.
			const historyData = [];
			const steps = [
				"pendiente_documentacion",
				"en_revision_documentacion",
				"pendiente_finiquito",
				"en_revision_finiquito",
				"pagado"
			];
			const currentStepIndex = steps.indexOf(siniestroData?.status);

			for (let i = 0; i <= currentStepIndex; i++) {
				historyData.push({
					step: steps[i],
					data: siniestroData
				});
			}
			setHistory(historyData);
		}
	}, [siniestroData]);


	const documentosFiniquito = [
		{ "value": "documento_finiquito", "label": <span>Documento de finiquito <a target="_blank" style={{textDecoration:"underline", color:"blue"}} href={process.env.REACT_APP_FILE_URL+siniestroData?.formato_finiquito?.path}>Descargar formato</a></span> },
		{ "value": "conoce_tu_cliente", "label": <span>Conoce a tu cliente <a target="_blank" style={{textDecoration:"underline", color:"blue"}} href="/docs/conoce_cliente.pdf">Descargar formato</a></span> },
		{ "value": "formato_transferencia", "label": <span>Formato de transferencia <a target="_blank" style={{textDecoration:"underline", color:"blue"}} href="/docs/formato_transferencia.pdf">Descargar formato</a></span> },
		{ "value": "acta_constitutiva", "label": "Acta constitutiva" },
		{ "value": "poder_notarial", "label": "Poder notarial para pleitos y cobranzas del representante legal." },
		{ "value": "csf_empresa", "label": "CSF de la empresa" },
		{ "value": "ine_representante_legel", "label": "INE del Representante legal." },
		{ "value": "comprobante_domicilio", "label": "Comprobante de domicilio no mayor a tres meses." },
		{ "value": "estado_cuenta", "label": "Estado de cuenta no mayor a tres meses." }
	]

	// Create crud actions for siniestro updates
	const siniestroCrudActions = crudFunctions(SINIESTRO);
	const { data: saveData, onFile, save, status: saveStatus, onChange } = useSave({
		data: siniestroData,
		crudActions: siniestroCrudActions,
		autoSave: true
	});

	const handleSubmitForReview = async () => {
		setIsSubmitting(true);
		try {
			await enviarSiniestroRevision(siniestroData?._id);
			queryClient.invalidateQueries(['siniestro', match.params.id]);
			queryClient.invalidateQueries(SINIESTRO.name);
			queryClient.invalidateQueries(SEGURO.name);
		} catch (error) {
			console.error('Error submitting for review:', error);
			alert(error?.errmsg || 'Error al enviar a revisión');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleStepClick = (step) => {
		const historicalStep = history.find(h => h.step === step);
		if (historicalStep) {
			setViewedStep(step);
		}
	};

	const viewedData = history.find(h => h.step === viewedStep)?.data || saveData;

	const documents = viewedData?.documentos || {};
	const documentosSubidosFiniquito = viewedData?.documentos_finiquito || {};
	const requiredDocuments = siniestroData?.tipo === "contenedor"
		? containerRequiredDocuments
		: generalRequiredDocuments;

	const allRequiredDocumentsUploaded = requiredDocuments
		.filter(doc => doc.required)
		.every(doc => (saveData?.documentos || {})[doc.key]?.files?.length > 0);

	const canSubmitForReview = allRequiredDocumentsUploaded && saveData?.status === 'pendiente_documentacion';

	return (
			<div className="p-6">
				<AutoSaveStatus status={saveStatus} />
				<h2 className="text-3xl font-bold text-blue mb-6">
					Gestión de Siniestro #{saveData?.num_siniestro || saveData?.id}
				</h2>

				<SiniestroProgressBar
					currentStatus={saveData?.status}
					onStepClick={handleStepClick}
					viewedStep={viewedStep}
				/>

				<div className="bg-gray-50 p-4 rounded-lg mb-6">
					<h3 className="text-lg font-semibold mb-3">Información del Siniestro</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700">Fecha del siniestro:</label>
							<DateComponent date={viewedData?.fecha_siniestro} />
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700">Valor asegurado:</label>
							<span>{format(viewedData?.valor_mercancia)}</span>
						</div>
						{viewedData?.nombre_conductor && (
							<div>
								<label className="block text-sm font-medium text-gray-700">Conductor:</label>
								<span>{viewedData.nombre_conductor}</span>
							</div>
						)}
						{viewedData?.placas_unidad && (
							<div>
								<label className="block text-sm font-medium text-gray-700">Placas:</label>
								<span>{viewedData.placas_unidad}</span>
							</div>
						)}
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700">Emails para notificaciones (separados por coma)</label>
							<Text
								className="bg-white rounded"
								name="correos_notificaciones"
								value={saveData?.correos_notificaciones}
								onChange={onChange}
								readOnly={viewedStep !== saveData?.status}
							/>
						</div>
					</div>

					{viewedData?.explicacion_siniestro && (
						<div className="mt-4">
							<label className="block text-sm font-medium text-gray-700">Explicación:</label>
							<p className="mt-1">{viewedData.explicacion_siniestro}</p>
						</div>
					)}

				</div>

				{/* Tabla de Seguros Asociados */}
				{siniestroData?.seguros?.length > 0 && (
					<div className="bg-white border rounded-lg p-4 mb-6">
						<h3 className="text-lg font-semibold mb-3">
							Seguros Asociados ({siniestroData.seguros.length})
						</h3>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell><strong># Seguro</strong></TableCell>
									<TableCell><strong>Tipo</strong></TableCell>
									<TableCell><strong>Fecha Recolección</strong></TableCell>
									<TableCell align="right"><strong>Valor Asegurado</strong></TableCell>
									<TableCell align="center"><strong>Póliza</strong></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{siniestroData.seguros.map((seg, index) => (
									<TableRow key={seg?._id || index}>
										<TableCell>{seg?.id || '-'}</TableCell>
										<TableCell>{seg?.tipo_seguro || '-'}</TableCell>
										<TableCell>
											{seg?.fecha_recoleccion ? <DateComponent date={seg.fecha_recoleccion} /> : '-'}
										</TableCell>
										<TableCell align="right">
											{format(seg?.valor_mercancia || 0)}
										</TableCell>
										<TableCell align="center">
											{seg?.poliza?.path ? (
												<IconButton
													color="primary"
													size="small"
													onClick={() => window.open(`${process.env.REACT_APP_FILE_URL}${seg.poliza.path}`, '_blank')}
												>
													<GetAppIcon fontSize="small" />
												</IconButton>
											) : '-'}
										</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell colSpan={4}><strong>Total</strong></TableCell>
									<TableCell align="right">
										<strong>{format(siniestroData.seguros.reduce((acc, s) => acc + (s?.valor_mercancia || 0), 0))}</strong>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</div>
				)}

				{viewedStep ==="pendiente_documentacion" && <>
					<div className="mb-6">
					<h3 className="text-lg font-semibold mb-4">Documentos Requeridos</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{requiredDocuments.map((doc) => (
							<div key={doc.key} className="border rounded-lg p-4 flex flex-col">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									{doc.label} {doc.required && <span className="text-red-500">*</span>}
								</label>
								<label className="block text-xs font-medium text-gray-700 mb-2">
									{doc.notes}
								</label>
								<div className="flex-grow">
									<File
										name={`documentos.${doc.key}.files`}
										value={documents[doc.key]?.files || []}
										onFile={onFile}
										download
										multipleFiles
										readOnly={documents[doc.key]?.status==="accepted" || viewedStep !== saveData?.status}
									/>
								</div>
								{documents[doc.key] && (
									<DocumentStatus
										status={documents[doc.key].status}
										notes={documents[doc.key].notes}
									/>
								)}
							</div>
						))}
					</div>
					</div>

					{saveData?.status === 'pendiente_documentacion' && (
						<>
							<div className="flex justify-between items-start">
								<Button
									variant="contained"
									color="primary"
									onClick={handleSubmitForReview}
									disabled={isSubmitting || !canSubmitForReview}
								>
									{isSubmitting ? <CircularProgress size={20} /> : 'Enviar a Revisión'}
								</Button>
							</div>

							{!allRequiredDocumentsUploaded && (
								<div className="mt-4 text-sm text-orange-600 bg-orange-50 p-3 rounded">
									<strong>Nota:</strong> Todos los documentos marcados con * deben tener al menos un archivo para poder enviar el siniestro a revisión.
								</div>
							)}
						</>
					)}
				</>}
				{viewedStep === "en_revision_documentacion" && (
					<div className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4" role="alert">
						<p className="font-bold">En Revisión</p>
						<p>Sus documentos han sido enviados y están siendo revisados por nuestro equipo. Se le notificará cualquier actualización.</p>
					</div>
				)}
				{viewedStep ==="pendiente_finiquito" && <>
					{saveData?.tipo==="paqueteria" && <div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700">Banco para deposito</label>
							<Text
								className="rounded"
								name="paqueteria.banco"
								value={saveData?.paqueteria?.banco}
								onChange={onChange}
							/>
						</div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700">Nombre a la que esta la cuenta de banco</label>
							<Text
								className="rounded"
								name="paqueteria.nombre_cuenta"
								value={saveData?.paqueteria?.nombre_cuenta}
								onChange={onChange}
							/>
						</div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700">Núm de cuenta clabe</label>
							<Text
								className="rounded"
								name="paqueteria.cuenta_clabe"
								value={saveData?.paqueteria?.cuenta_clabe}
								onChange={onChange}
							/>
						</div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700">Escanea el siguiente Código con tu telefono para enviar el resto de la documentación</label>
							<QRCode value={`https://c.vink.com.mx/siniestro-mobile/${saveData._id}`} />

						</div>
						
					</div>}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{viewedData?.documentos_requeridos_finiquito?.map((key) => {
							const doc = documentosFiniquito.find(d=>d.value===key)
							return <div key={key} className="border rounded-lg p-4 flex flex-col">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									{doc.label} <span className="text-red-500">*</span>
								</label>
								<div className="flex-grow">
									<File
										name={`documentos_finiquito.${doc.value}.files`}
										value={documentosSubidosFiniquito[doc.value]?.files || []}
										onFile={onFile}
										download
										multipleFiles
										readOnly={documentosSubidosFiniquito[doc.value]?.status==="accepted" || viewedStep !== saveData?.status}
									/>
								</div>
								{documentosSubidosFiniquito[doc.value] && (
									<DocumentStatus
										status={documentosSubidosFiniquito[doc.value].status}
										notes={documentosSubidosFiniquito[doc.value].notes}
									/>
								)}
							</div>
						})}
						{saveData?.status === 'pendiente_finiquito' && (
							<div className="flex justify-between items-start">
								<Button
									variant="contained"
									color="primary"
									onClick={async ()=>{
										try {
											await enviarSiniestroRevisionFiniquito(siniestroData?._id);
											queryClient.invalidateQueries(['siniestro', match.params.id]);
											queryClient.invalidateQueries(SINIESTRO.name);
											queryClient.invalidateQueries(SEGURO.name);
										} catch(e) {
											alert(e?.errmsg || 'Error al enviar finiquito a revisión');
										}
									}}
									disabled={isSubmitting}
								>
									{isSubmitting ? <CircularProgress size={20} /> : 'Enviar a Revisión'}
								</Button>
							</div>
						)}

					</div>

				</>}
				{viewedStep === "en_revision_finiquito" && (
					<div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700 p-4" role="alert">
						<p className="font-bold">En espera de pago</p>
						<p>La documentación del finiquito está completa y en revisión final. El pago está siendo procesado.</p>
					</div>
				)}
				{viewedStep === "pagado" && (
					<div className="text-center p-6 bg-green-50 rounded-lg">
						<h3 className="text-2xl font-bold text-green-800">Pago Realizado Exitosamente</h3>
						<p className="text-green-600 mt-2">El pago de su siniestro ha sido completado.</p>
						{viewedData?.comprobante_pago && (
							<div className="mt-4">
								<p className="text-sm font-medium text-gray-700 mb-2">Comprobante de Pago:</p>
								<File
									value={viewedData.comprobante_pago}
									download
									readOnly
								/>
							</div>
						)}
					</div>
				)}
		</div>
	);
};

export default SiniestroModal;
